/**
 * Catalog — per-site catalog-management surface (ADR-0103). Restructured
 * after a product/UX review found the original four-equal-cards layout
 * cluttered. Top to bottom:
 *
 *   1. Page header — kicker + serif h1 + subtitle.
 *   2. PRODUCTS table (the hero, full width) with a toolbar in the card
 *      header: server-side search (debounced), sortable column headers,
 *      and a column-visibility menu. The eligibility cell collapses the two
 *      search/checkout pills into ONE effective-state pill with the
 *      breakdown + a derived reason in a tooltip.
 *   3. DATA SOURCE strip — a thin bordered strip directly under the table:
 *      ghost "Sync from Shopify" (full sync-now behaviour preserved),
 *      a "last synced" caption, ghost "Upload CSV" (import preserved), and
 *      a client-side "Download template" link.
 *   4. Secondary band — Eligibility + Compliance side by side (md:grid-cols-2).
 *      Compliance collapses to a compact summary when complete.
 *   5. First run (0 products) — the eligibility + compliance band is
 *      suppressed and a single EmptyStateCard carries the two data-source
 *      actions.
 *
 * Design rules applied (CLAUDE.md §4): lime is the SINGLE accent — at most
 * one lime button per scroll region. The eligibility "Accept & activate" is
 * the one lime decision; sync / upload / re-apply are demoted to ghost. Table
 * `th` uses `C.dim` (matches the dashboard reference). All four endpoints are
 * owner/admin-only — a `forbidden` result renders a clear gate.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  AlertOctagon,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Download,
  ExternalLink,
  Pencil,
  RefreshCw,
  Trash2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Upload,
} from 'lucide-react';

import type { CatalogProduct, UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import {
  Card,
  EmptyStateCard,
  Modal,
  NumLabel,
  Pill,
  Tooltip,
} from '../../components/atoms/index.js';
import {
  activateEligibility,
  deleteCatalogProduct,
  downloadCatalogFeed,
  getCatalog,
  getCompliance,
  getEligibility,
  getShopifyConnected,
  importCatalogCsv,
  previewEligibility,
  putCompliance,
  syncNow,
  updateCatalogProduct,
  type CatalogImportSummary,
  type CatalogPage,
  type CatalogProductEdit,
  type CatalogSortColumn,
  type CatalogSortDir,
  type DeleteProductResult,
  type EligibilityState,
  type SiteComplianceFields,
  type UpdateProductResult,
} from '../../api/catalog.js';
import {
  availabilityTone,
  deriveSearchIneligibleReason,
  formatAvailability,
  formatInventory,
  formatLastSynced,
  formatProductKind,
  formatProductLastSynced,
  formatProductPrice,
  formatSalePrice,
  formatSource,
  isEditableSource,
  safeStoreUrl,
  syncStateLabel,
  CATALOG_TEMPLATE_CSV,
} from './display.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/** Rows-per-page options + the default. 100 in one page is a lot of scroll. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
/** Max CSV the server accepts (mirrors the route's 8 MB cap). */
const MAX_CSV_BYTES = 8 * 1024 * 1024;

/** Debounce for the table search input → catalog refetch. */
const SEARCH_DEBOUNCE_MS = 300;

/** Manual-sync poll cadence + cap. The background sync is bounded (≤20
 *  GraphQL pages); we poll the catalog list every ~3s up to ~10 times,
 *  watching for `last_synced_at` to advance past the snapshot taken when
 *  the sync started. */
const SYNC_POLL_INTERVAL_MS = 3000;
const SYNC_POLL_MAX_ATTEMPTS = 10;

type ThemeC = ReturnType<typeof useC>;

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; page: CatalogPage }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/** The sortable columns + their display labels (the backend whitelist subset
 *  the table exposes). */
type SortKey =
  | 'title'
  | 'brand'
  | 'price_minor'
  | 'availability'
  | 'inventory_quantity'
  | 'is_eligible_search';

/** Column identity for the visibility menu. `title` is always shown. */
type ColumnKey =
  | 'title'
  | 'brand'
  | 'item_id'
  | 'price'
  | 'sale_price'
  | 'availability'
  | 'inventory'
  | 'gtin'
  | 'eligibility'
  | 'kind'
  | 'source'
  | 'last_synced';

/** Grouping for the Columns menu — keeps the (now longer) list tidy. */
type ColumnGroup = 'core' | 'identity' | 'commerce' | 'governance' | 'sync';

interface ColumnDef {
  readonly key: ColumnKey;
  readonly label: string;
  /** Backend sort column, when the column is sortable. */
  readonly sort?: CatalogSortColumn;
  /** Always visible (cannot be hidden). */
  readonly required?: boolean;
  /** Section in the show/hide menu. */
  readonly group: ColumnGroup;
}

const COLUMNS: readonly ColumnDef[] = [
  // Core (default-visible) — the lean default set.
  { key: 'title', label: 'Title', sort: 'title', required: true, group: 'core' },
  { key: 'price', label: 'Price', sort: 'price_minor', group: 'core' },
  { key: 'availability', label: 'Availability', sort: 'availability', group: 'core' },
  { key: 'eligibility', label: 'Eligibility', sort: 'is_eligible_search', group: 'core' },
  // Identity (opt-in).
  { key: 'brand', label: 'Brand', sort: 'brand', group: 'identity' },
  { key: 'item_id', label: 'SKU', group: 'identity' },
  { key: 'gtin', label: 'GTIN', group: 'identity' },
  // Commerce (opt-in).
  { key: 'sale_price', label: 'Sale price', group: 'commerce' },
  { key: 'inventory', label: 'Inventory', sort: 'inventory_quantity', group: 'commerce' },
  { key: 'kind', label: 'Kind', group: 'commerce' },
  // Provenance / freshness (opt-in). "Last synced" carries the freshness
  // signal: it shows the relative sync time, and flags a row "Stale · …"
  // when it was not seen in the latest sync — so a separate always-"synced"
  // state column is redundant noise and was dropped.
  { key: 'source', label: 'Source', group: 'sync' },
  { key: 'last_synced', label: 'Last synced', group: 'sync' },
];

/** Default-visible columns: Title / Price / Availability / Eligibility only.
 *  Everything else (identity, commerce, provenance/freshness) is opt-in via
 *  the Columns menu. */
const DEFAULT_VISIBLE: Readonly<Record<ColumnKey, boolean>> = {
  title: true,
  price: true,
  availability: true,
  eligibility: true,
  brand: false,
  item_id: false,
  gtin: false,
  sale_price: false,
  inventory: false,
  kind: false,
  source: false,
  last_synced: false,
};

/** Ordered group headings for the Columns menu. */
const COLUMN_GROUPS: readonly { readonly key: ColumnGroup; readonly label: string }[] = [
  { key: 'identity', label: 'Identity' },
  { key: 'commerce', label: 'Commerce' },
  { key: 'sync', label: 'Provenance & sync' },
];

export interface CatalogProps {
  /** Optional role override for tests that don't wire `<AuthProvider />`. */
  readonly role_override?: UserRole;
}

export function Catalog({ role_override }: CatalogProps = {}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const { activeSite } = useSites();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;
  const canUse = role === 'owner' || role === 'admin';

  const site_id = activeSite?.site_id ?? null;

  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ col: SortKey; dir: CatalogSortDir } | null>(null);
  // Page size (rows per page). Default 25 — a full 100 is a lot of scroll.
  // Read via a ref inside loadList so changing it doesn't churn the fetch
  // effects' deps; the selector handler resets to page 0 and reloads.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const pageSizeRef = useRef(DEFAULT_PAGE_SIZE);
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Whether the site has any catalog eligibility rules configured. Lifted up
  // from the eligibility section so the table's eligibility-pill tooltip can
  // derive "Eligibility not activated yet" vs "Not matched by the rules".
  const [hasCatalogRules, setHasCatalogRules] = useState(false);

  // Whether the site has a CONNECTED Shopify integration. Gates the
  // "Sync from Shopify" control — a site with no Shopify connection can only
  // populate its catalog via CSV, so offering a Shopify sync there is
  // misleading. `null` = not yet probed (hide the button until we know).
  const [shopifyConnected, setShopifyConnected] = useState<boolean | null>(null);

  const loadList = useCallback(
    async (
      nextOffset: number,
      nextSearch: string,
      nextSort: { col: SortKey; dir: CatalogSortDir } | null,
    ): Promise<void> => {
      if (http === null || site_id === null) return;
      setList({ kind: 'loading' });
      const res = await getCatalog(http, site_id, {
        limit: pageSizeRef.current,
        offset: nextOffset,
        ...(nextSearch.trim().length > 0 ? { search: nextSearch } : {}),
        ...(nextSort !== null ? { sort: nextSort.col, dir: nextSort.dir } : {}),
      });
      switch (res.kind) {
        case 'ok':
          setList({ kind: 'ready', page: res.page });
          break;
        case 'forbidden':
          setList({ kind: 'forbidden' });
          break;
        case 'not_found':
          setList({ kind: 'error', message: 'Site not found.' });
          break;
        case 'error':
          setList({ kind: 'error', message: res.message });
          break;
      }
    },
    [http, site_id],
  );

  // Fetch-on-mount + on site change. Resets paging / search / sort.
  useEffect(() => {
    if (!canUse) {
      setList({ kind: 'forbidden' });
      return;
    }
    if (http === null || site_id === null) return;
    setOffset(0);
    setSearch('');
    setSort(null);
    void loadList(0, '', null);
  }, [canUse, http, site_id, loadList]);

  // Probe the Shopify connection (gates the Sync-from-Shopify control), once
  // per site. Independent of the catalog list load; defaults to hidden until
  // resolved + on any error (fail-safe).
  useEffect(() => {
    if (!canUse || http === null || site_id === null) {
      setShopifyConnected(null);
      return;
    }
    let alive = true;
    setShopifyConnected(null);
    void getShopifyConnected(http, site_id).then((connected) => {
      if (alive) setShopifyConnected(connected);
    });
    return () => {
      alive = false;
    };
  }, [canUse, http, site_id]);

  // Debounced search → refetch at page 0. Skips the very first run (the mount
  // effect already loaded). A ref tracks the last-applied search so a sort/page
  // change doesn't re-trigger this.
  const firstSearchRef = useRef(true);
  useEffect(() => {
    if (firstSearchRef.current) {
      firstSearchRef.current = false;
      return;
    }
    if (!canUse || http === null || site_id === null) return;
    const handle = setTimeout(() => {
      setOffset(0);
      void loadList(0, search, sort);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // `sort` intentionally read (not a dep): a sort change has its own handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, canUse, http, site_id, loadList]);

  const goToPage = useCallback(
    (nextOffset: number): void => {
      const clamped = Math.max(0, nextOffset);
      setOffset(clamped);
      void loadList(clamped, search, sort);
    },
    [loadList, search, sort],
  );

  // Change rows-per-page → reset to page 0 and reload. The ref is set
  // synchronously so the immediate loadList uses the new size.
  const onPageSize = useCallback(
    (size: number): void => {
      pageSizeRef.current = size;
      setPageSize(size);
      setOffset(0);
      void loadList(0, search, sort);
    },
    [loadList, search, sort],
  );

  // Toggle/cycle sort on a column header. First click → asc; re-click → desc;
  // resets to page 0. Computed from the current `sort` (no side effects inside
  // the state updater).
  const onSort = useCallback(
    (col: SortKey): void => {
      const next: { col: SortKey; dir: CatalogSortDir } =
        sort !== null && sort.col === col
          ? { col, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
          : { col, dir: 'asc' };
      setSort(next);
      setOffset(0);
      void loadList(0, search, next);
    },
    [loadList, search, sort],
  );

  const refreshAll = useCallback((): void => {
    setOffset(0);
    void loadList(0, search, sort);
  }, [loadList, search, sort]);

  // Stable closures for the eligibility + compliance sections. These sections
  // reload when their `load` prop's identity changes; without memoization a
  // fresh arrow on every parent re-render (e.g. when `hasCatalogRules` updates)
  // would re-trigger their loads and flicker their forms.
  const eligLoad = useCallback(
    () => getEligibility(http!, site_id!),
    [http, site_id],
  );
  const eligPreview = useCallback(
    (rules: readonly import('@sill/types').Rule[]) => previewEligibility(http!, site_id!, rules),
    [http, site_id],
  );
  const eligActivate = useCallback(
    (rules: readonly import('@sill/types').Rule[]) => activateEligibility(http!, site_id!, rules),
    [http, site_id],
  );
  const complianceLoad = useCallback(
    () => getCompliance(http!, site_id!),
    [http, site_id],
  );
  const complianceSave = useCallback(
    (update: import('../../api/catalog.js').ComplianceUpdate) => putCompliance(http!, site_id!, update),
    [http, site_id],
  );
  const dataSourceStartSync = useCallback(() => syncNow(http!, site_id!), [http, site_id]);
  const dataSourcePoll = useCallback(
    () => getCatalog(http!, site_id!, { limit: pageSizeRef.current, offset: 0 }),
    [http, site_id],
  );
  const dataSourceUpload = useCallback(
    (csv: string) => importCatalogCsv(http!, site_id!, csv),
    [http, site_id],
  );
  const editProductFn = useCallback(
    (id: string, fields: CatalogProductEdit) => updateCatalogProduct(http!, site_id!, id, fields),
    [http, site_id],
  );
  const deleteProductFn = useCallback(
    (id: string) => deleteCatalogProduct(http!, site_id!, id),
    [http, site_id],
  );
  const downloadFeedFn = useCallback(() => downloadCatalogFeed(http!, site_id!), [http, site_id]);

  if (!activeSite) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <EmptyStateCard
          icon={Boxes}
          label="No active site"
          hint="Pick or add a site from the Sites view before managing its catalog."
        />
      </div>
    );
  }

  const isReady = list.kind === 'ready';
  const isEmpty = list.kind === 'ready' && list.page.products.length === 0;
  // First run = no search active AND zero products. (A search that returns no
  // rows is NOT first-run — we keep the toolbar + data-source band.)
  const isFirstRun = isEmpty && search.trim().length === 0;
  const lastSyncedAt = isReady ? list.page.last_synced_at : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={1}>{activeSite.domain} · products</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(36px, 6vw, 52px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          What agents <em>see</em>.
        </h1>
        <div
          className="mt-2"
          style={{ fontFamily: sansFont, fontSize: 14, color: C.muted, lineHeight: 1.5 }}
        >
          The products and compliance metadata projected to verified agents for this
          site. Connect a cart or upload a CSV to populate it.
        </div>
      </div>

      {!canUse ? (
        <ForbiddenCard C={C} />
      ) : (
        <div className="space-y-6">
          {/* Data-source controls (Sync from Shopify / Upload CSV / last
              synced) sit ABOVE the table — co-located with the data they
              refresh, the way a toolbar reads. */}
          {http !== null && site_id !== null ? (
            <DataSourceStrip
              C={C}
              shopifyConnected={shopifyConnected === true}
              lastSyncedAt={lastSyncedAt}
              startSync={dataSourceStartSync}
              pollCatalog={dataSourcePoll}
              currentPage={isReady ? list.page : null}
              uploadCsv={dataSourceUpload}
              onSynced={refreshAll}
              onImported={refreshAll}
            />
          ) : null}

          <ProductListSection
            C={C}
            state={list}
            offset={offset}
            search={search}
            sort={sort}
            hasCatalogRules={hasCatalogRules}
            pageSize={pageSize}
            onSearch={setSearch}
            onSort={onSort}
            onPage={goToPage}
            onPageSize={onPageSize}
            editProduct={editProductFn}
            deleteProduct={deleteProductFn}
            downloadFeed={downloadFeedFn}
            onMutated={refreshAll}
          />

          {/* First run suppresses the eligibility + compliance band; the
              EmptyStateCard inside the table plus the data-source strip
              above carry the only CTAs. */}
          {http !== null && site_id !== null && !isFirstRun ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EligibilitySection
                C={C}
                load={eligLoad}
                preview={eligPreview}
                activate={eligActivate}
                onActivated={refreshAll}
                onConfiguredChange={setHasCatalogRules}
              />
              <ComplianceSection
                C={C}
                load={complianceLoad}
                save={complianceSave}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Forbidden card — shared owner/admin gate copy.
// ---------------------------------------------------------------------------

function ForbiddenCard({ C }: { C: ThemeC }): JSX.Element {
  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <ShieldCheck size={16} color={C.muted} className="flex-shrink-0 mt-0.5" />
        <div>
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 600 }}>
            Requires owner or admin
          </div>
          <div
            className="mt-1"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
          >
            Viewing and editing this site&apos;s catalog and compliance fields is
            limited to owner and admin roles. Ask an owner or admin for access.
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Products table (hero) — toolbar (search / sortable headers / columns) +
// the collapsed single eligibility pill.
// ---------------------------------------------------------------------------

function ProductListSection({
  C,
  state,
  offset,
  search,
  sort,
  hasCatalogRules,
  pageSize,
  onSearch,
  onSort,
  onPage,
  onPageSize,
  editProduct,
  deleteProduct,
  downloadFeed,
  onMutated,
}: {
  C: ThemeC;
  state: ListState;
  offset: number;
  search: string;
  sort: { col: SortKey; dir: CatalogSortDir } | null;
  hasCatalogRules: boolean;
  pageSize: number;
  onSearch: (value: string) => void;
  onSort: (col: SortKey) => void;
  onPage: (nextOffset: number) => void;
  onPageSize: (size: number) => void;
  editProduct: (id: string, fields: CatalogProductEdit) => Promise<UpdateProductResult>;
  deleteProduct: (id: string) => Promise<DeleteProductResult>;
  downloadFeed: () => Promise<import('../../api/catalog.js').DownloadFeedResult>;
  onMutated: () => void;
}): JSX.Element {
  const [visible, setVisible] = useState<Record<ColumnKey, boolean>>(() => ({
    ...DEFAULT_VISIBLE,
  }));
  const [columnsOpen, setColumnsOpen] = useState(false);
  // Per-row edit/delete modal targets.
  const [editing, setEditing] = useState<CatalogProduct | null>(null);
  const [deleting, setDeleting] = useState<CatalogProduct | null>(null);

  const shownColumns = useMemo(
    () => COLUMNS.filter((c) => c.required === true || visible[c.key]),
    [visible],
  );

  const [feedBusy, setFeedBusy] = useState(false);
  const onDownloadFeed = useCallback(async (): Promise<void> => {
    setFeedBusy(true);
    const res = await downloadFeed();
    setFeedBusy(false);
    if (res.kind !== 'ok') return;
    const url = URL.createObjectURL(res.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'openai-agent-feed.jsonl';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [downloadFeed]);

  return (
    <Card>
      <div
        className="px-5 py-4 flex items-center justify-between flex-wrap gap-3"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <div className="flex items-center gap-2">
          <Boxes size={14} color={C.text} />
          <span style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 600 }}>
            Products
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <SearchInput C={C} value={search} onChange={onSearch} />
          <Tooltip content="Download the OpenAI agent feed (JSONL) — the eligible products as agents see them">
          <button
            type="button"
            onClick={() => void onDownloadFeed()}
            disabled={feedBusy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5"
            style={{
              background: 'transparent',
              color: C.muted,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              cursor: feedBusy ? 'not-allowed' : 'pointer',
              opacity: feedBusy ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            <Download size={13} />
            {feedBusy ? 'Preparing…' : 'Agent feed'}
          </button>
          </Tooltip>
          <ColumnsButton
            C={C}
            open={columnsOpen}
            onToggle={() => setColumnsOpen((o) => !o)}
            onClose={() => setColumnsOpen(false)}
            visible={visible}
            onVisibleChange={setVisible}
          />
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div className="px-5 py-6" style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Loading catalog…
        </div>
      ) : null}

      {state.kind === 'forbidden' ? <div className="p-2"><ForbiddenCard C={C} /></div> : null}

      {state.kind === 'error' ? (
        <div className="px-5 py-6" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5 }}>
          Couldn&apos;t load the catalog — {state.message}
        </div>
      ) : null}

      {state.kind === 'ready' && state.page.products.length === 0 ? (
        search.trim().length > 0 ? (
          <EmptyStateCard
            icon={Search}
            label="No matches"
            hint="No products match this search. Clear it to see the full catalog."
          />
        ) : (
          <EmptyStateCard
            icon={Boxes}
            label="No catalog products yet"
            hint="How products become discoverable: sync or upload, then activate eligibility for search. Checkout is separate — it needs a connected payment rail, compliance, and a checkout rule (offered once those are in place). Start with Sync from Shopify or Upload CSV above."
          />
        )
      ) : null}

      {state.kind === 'ready' && state.page.products.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {shownColumns.map((col) => {
                    const colSort = col.sort;
                    return (
                      <SortableTh
                        key={col.key}
                        C={C}
                        label={col.label}
                        sortable={colSort !== undefined}
                        active={sort !== null && colSort === sort.col}
                        dir={sort?.dir ?? 'asc'}
                        {...(colSort !== undefined
                          ? { onClick: () => onSort(colSort as SortKey) }
                          : {})}
                      />
                    );
                  })}
                  {/* Actions column — always last, not toggleable. */}
                  <th
                    style={{
                      padding: '10px 16px',
                      textAlign: 'right',
                      fontFamily: monoFont,
                      fontSize: 10,
                      letterSpacing: 1,
                      color: C.dim,
                      borderBottom: `1px solid ${C.border}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {' '}
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.page.products.map((p) => (
                  <ProductRow
                    key={p.catalog_product_id}
                    C={C}
                    product={p}
                    columns={shownColumns}
                    hasCatalogRules={hasCatalogRules}
                    onEdit={() => setEditing(p)}
                    onDelete={() => setDeleting(p)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            C={C}
            offset={offset}
            count={state.page.products.length}
            pageSize={pageSize}
            hasMore={state.page.has_more}
            onPage={onPage}
            onPageSize={onPageSize}
          />
        </>
      ) : null}

      {editing !== null ? (
        <EditProductModal
          C={C}
          product={editing}
          onClose={() => setEditing(null)}
          editProduct={editProduct}
          onSaved={() => {
            setEditing(null);
            onMutated();
          }}
        />
      ) : null}
      {deleting !== null ? (
        <DeleteProductModal
          C={C}
          product={deleting}
          onClose={() => setDeleting(null)}
          deleteProduct={deleteProduct}
          onDeleted={() => {
            setDeleting(null);
            onMutated();
          }}
        />
      ) : null}
    </Card>
  );
}

/** Slim search input matching the existing TextField look. */
function SearchInput({
  C,
  value,
  onChange,
}: {
  C: ThemeC;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="relative inline-flex items-center">
      <Search
        size={13}
        color={C.muted}
        style={{ position: 'absolute', left: 8, pointerEvents: 'none' }}
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search title, SKU, brand"
        aria-label="Search catalog"
        autoComplete="off"
        spellCheck={false}
        style={{
          width: 220,
          padding: '6px 10px 6px 28px',
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          color: C.text,
          fontFamily: sansFont,
          fontSize: 12,
          outline: 'none',
        }}
      />
    </div>
  );
}

/** Ghost "Columns" button + a Modal of checkboxes for column visibility. */
function ColumnsButton({
  C,
  open,
  onToggle,
  onClose,
  visible,
  onVisibleChange,
}: {
  C: ThemeC;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  visible: Record<ColumnKey, boolean>;
  onVisibleChange: (next: Record<ColumnKey, boolean>) => void;
}): JSX.Element {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-label="Columns"
        className="inline-flex items-center gap-1.5 px-3 py-1.5"
        style={{
          background: 'transparent',
          color: C.text,
          border: `1px solid ${C.border}`,
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1,
          borderRadius: 2,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <SlidersHorizontal size={11} />
        COLUMNS
      </button>
      <Modal open={open} onClose={onClose} size="sm" title="Columns" subtitle="Choose which columns to show.">
        <div className="p-6 space-y-5">
          {COLUMN_GROUPS.map((group) => {
            const cols = COLUMNS.filter(
              (c) => c.required !== true && c.group === group.key,
            );
            if (cols.length === 0) return null;
            return (
              <div key={group.key} className="space-y-3">
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    letterSpacing: 1.5,
                    color: C.dim,
                  }}
                >
                  {group.label.toUpperCase()}
                </div>
                {cols.map((col) => {
                  const checked = visible[col.key];
                  return (
                    <label
                      key={col.key}
                      className="flex items-center gap-3 cursor-pointer"
                      style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        aria-label={col.label}
                        onChange={(e) =>
                          onVisibleChange({ ...visible, [col.key]: e.target.checked })
                        }
                      />
                      {col.label}
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}

/** A `th` matching the dashboard reference (mono 9 / letter-spacing / C.dim),
 *  made sortable by a color shift + caret rather than a restyle. */
function SortableTh({
  C,
  label,
  sortable,
  active,
  dir,
  onClick,
}: {
  C: ThemeC;
  label: string;
  sortable: boolean;
  active: boolean;
  dir: CatalogSortDir;
  onClick?: () => void;
}): JSX.Element {
  const Caret = dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      onClick={onClick}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
      style={{
        textAlign: 'left',
        padding: '10px 16px',
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1.5,
        fontWeight: 400,
        color: active ? C.text : C.dim,
        borderBottom: `1px solid ${C.borderSoft}`,
        whiteSpace: 'nowrap',
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <span className="inline-flex items-center gap-1">
        {label.toUpperCase()}
        {sortable && active ? <Caret size={11} /> : null}
      </span>
    </th>
  );
}

function ProductRow({
  C,
  product,
  columns,
  hasCatalogRules,
  onEdit,
  onDelete,
}: {
  C: ThemeC;
  product: CatalogProduct;
  columns: readonly ColumnDef[];
  hasCatalogRules: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const editable = isEditableSource(product.source);
  return (
    <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
      {columns.map((col) => {
        switch (col.key) {
          case 'title': {
            // The storefront link replaces the old raw item_id/GID subtitle.
            // `product.url` is merchant-controlled, so it is scheme-guarded
            // (http/https only) before it can become an href.
            const storeUrl = safeStoreUrl(product.url);
            return (
              <td key={col.key} style={{ padding: '12px 16px', maxWidth: 280 }}>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={product.title} /* title-ok: full value of the ellipsised title */
                >
                  {product.title}
                </div>
                {storeUrl !== undefined ? (
                  <a
                    href={storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={storeUrl} /* title-ok: full URL behind the link label */
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 2,
                      fontFamily: sansFont,
                      fontSize: 11,
                      color: C.muted,
                      textDecoration: 'none',
                    }}
                  >
                    View on store
                    <ExternalLink size={10} />
                  </a>
                ) : null}
              </td>
            );
          }
          case 'brand':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: sansFont, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}
              >
                {product.brand ?? '—'}
              </td>
            );
          case 'item_id':
            // The merchant-facing SKU — NEVER the upstream id/GID (`item_id`),
            // which is internal upsert plumbing. An em-dash when the source
            // reported no SKU.
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}
              >
                {product.sku ?? '—'}
              </td>
            );
          case 'gtin':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}
              >
                {product.gtin ?? '—'}
              </td>
            );
          case 'source':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: sansFont, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}
              >
                {formatSource(product.source)}
              </td>
            );
          case 'price':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: monoFont, fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}
              >
                {formatProductPrice(product)}
              </td>
            );
          case 'sale_price':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: monoFont, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}
              >
                {formatSalePrice(product)}
              </td>
            );
          case 'inventory':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: monoFont, fontSize: 12, color: C.text, whiteSpace: 'nowrap', textAlign: 'left' }}
              >
                {formatInventory(product)}
              </td>
            );
          case 'kind':
            return (
              <td
                key={col.key}
                style={{ padding: '12px 16px', fontFamily: sansFont, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}
              >
                {formatProductKind(product)}
              </td>
            );
          case 'last_synced': {
            // Freshness in one cell: the relative sync time, prefixed with
            // the state ("Stale · …") and tinted amber when the row was not
            // seen in the latest sync. (A 'synced' row shows just the time;
            // 'removed' rows are excluded from the list upstream.)
            const isStale = product.sync_state !== 'synced';
            return (
              <td
                key={col.key}
                style={{
                  padding: '12px 16px',
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: isStale ? C.amberFg : C.muted,
                  whiteSpace: 'nowrap',
                }}
              >
                {isStale
                  ? `${syncStateLabel(product.sync_state)} · ${formatProductLastSynced(product)}`
                  : formatProductLastSynced(product)}
              </td>
            );
          }
          case 'availability':
            return (
              <td key={col.key} style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                <Pill tone={availabilityTone(product.availability)}>
                  {formatAvailability(product.availability).toUpperCase()}
                </Pill>
              </td>
            );
          case 'eligibility':
            return (
              <td key={col.key} style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                <EligibilityCell C={C} product={product} hasCatalogRules={hasCatalogRules} />
              </td>
            );
        }
      })}
      {/* Actions — Edit (owned sources only) + Delete (all). */}
      <td style={{ padding: '8px 16px', whiteSpace: 'nowrap', textAlign: 'right' }}>
        <div className="inline-flex items-center gap-1">
          {editable ? (
            <Tooltip content="Edit product">
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit product"
              className="inline-flex items-center justify-center"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: C.muted }}
            >
              <Pencil size={13} />
            </button>
            </Tooltip>
          ) : (
            <Tooltip content={`Managed by ${formatSource(product.source)} — edit it there`} aria-label={`Managed by ${formatSource(product.source)}`}>
              <span style={{ display: 'inline-flex', padding: 4, color: C.dim, cursor: 'default' }}>
                <Pencil size={13} />
              </span>
            </Tooltip>
          )}
          <Tooltip content="Delete product">
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete product"
            className="inline-flex items-center justify-center"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: C.muted }}
          >
            <Trash2 size={13} />
          </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Edit / delete modals (per-row manual management)
// ---------------------------------------------------------------------------

const AVAILABILITY_OPTIONS = [
  '',
  'in_stock',
  'out_of_stock',
  'pre_order',
  'backorder',
  'unknown',
] as const;

/** Modal form to edit a manually-managed product's merchant-facing fields. */
function EditProductModal({
  C,
  product,
  onClose,
  editProduct,
  onSaved,
}: {
  C: ThemeC;
  product: CatalogProduct;
  onClose: () => void;
  editProduct: (id: string, fields: CatalogProductEdit) => Promise<UpdateProductResult>;
  onSaved: () => void;
}): JSX.Element {
  // Price is edited in MAJOR units (dollars); converted to minor on save.
  const initialPrice =
    product.price_minor !== undefined ? (product.price_minor / 100).toFixed(2) : '';
  const initialSku = product.sku ?? '';
  const initialBrand = product.brand ?? '';
  const initialCurrency = product.currency ?? '';
  const initialAvailability = product.availability ?? '';
  const initialInventory =
    product.inventory_quantity !== undefined ? String(product.inventory_quantity) : '';
  const initialDescription = product.description ?? '';
  const initialImageUrl = product.image_url ?? '';
  const initialUrl = product.url ?? '';
  const [title, setTitle] = useState(product.title);
  const [sku, setSku] = useState(initialSku);
  const [brand, setBrand] = useState(initialBrand);
  const [price, setPrice] = useState(initialPrice);
  const [currency, setCurrency] = useState(initialCurrency);
  const [availability, setAvailability] = useState(initialAvailability);
  const [inventory, setInventory] = useState(initialInventory);
  const [description, setDescription] = useState(initialDescription);
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [url, setUrl] = useState(initialUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Dirty guard — Escape on an edited product dialog now prompts
  // "Discard changes?" instead of unmounting the form silently.
  const dirty =
    title !== product.title
    || sku !== initialSku
    || brand !== initialBrand
    || price !== initialPrice
    || currency !== initialCurrency
    || availability !== initialAvailability
    || inventory !== initialInventory
    || description !== initialDescription
    || imageUrl !== initialImageUrl
    || url !== initialUrl;
  // Mount guard — the save handler awaits a PATCH, so a tab-close or
  // (previously) an outside-click dismiss while the request is in flight
  // would leave the ok/error setState running against an unmounted
  // instance. The guard is cheap and prevents a stale "Network error"
  // from latching on a success path if the dialog is unmounted mid-save.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    color: C.text,
    fontFamily: sansFont,
    fontSize: 13,
    outline: 'none',
  };
  const labelStyle: CSSProperties = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1,
    color: C.muted,
    marginBottom: 4,
    display: 'block',
  };

  const onSave = async (): Promise<void> => {
    setError(null);
    const t = title.trim();
    if (t.length === 0) {
      setError('Title is required.');
      return;
    }
    // Price: blank → clear; else parse dollars → integer minor units.
    let price_minor: number | null = null;
    const priceTrim = price.trim();
    if (priceTrim.length > 0) {
      const major = Number(priceTrim);
      if (!Number.isFinite(major) || major < 0) {
        setError('Price must be a non-negative number.');
        return;
      }
      price_minor = Math.round(major * 100);
    }
    let inventory_quantity: number | null = null;
    const invTrim = inventory.trim();
    if (invTrim.length > 0) {
      const n = Number(invTrim);
      if (!Number.isInteger(n) || n < 0) {
        setError('Inventory must be a non-negative whole number.');
        return;
      }
      inventory_quantity = n;
    }
    const cur = currency.trim();
    if (cur.length > 0 && !/^[A-Za-z]{3}$/.test(cur)) {
      setError('Currency must be a 3-letter code (e.g. USD).');
      return;
    }
    const fields: CatalogProductEdit = {
      title: t,
      sku: sku.trim() || null,
      brand: brand.trim() || null,
      description: description.trim() || null,
      image_url: imageUrl.trim() || null,
      url: url.trim() || null,
      price_minor,
      currency: cur ? cur.toUpperCase() : null,
      availability: availability ? availability : null,
      inventory_quantity,
    };
    setBusy(true);
    const res = await editProduct(product.catalog_product_id, fields);
    if (!aliveRef.current) return;
    setBusy(false);
    switch (res.kind) {
      case 'ok':
        onSaved();
        return;
      case 'source_managed':
        setError('This product is managed by a connected store — edit it there.');
        return;
      case 'not_found':
        setError('Product not found (it may have been removed).');
        return;
      case 'forbidden':
        setError('Editing requires owner or admin.');
        return;
      case 'bad_request':
        setError(res.reason !== undefined ? `Rejected: ${res.reason}.` : 'Invalid values.');
        return;
      case 'error':
        setError(res.message);
        return;
    }
  };

  const field = (label: string, node: JSX.Element): JSX.Element => (
    <div>
      <label style={labelStyle}>{label}</label>
      {node}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Edit product"
      subtitle="Changes apply to this catalog row."
      closeOnBackdrop={false}
      dirty={dirty}
    >
      <div className="flex flex-col gap-3 p-6" data-testid="edit-product-form">
        {field('TITLE', (
          <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
        ))}
        <div className="grid grid-cols-2 gap-3">
          {field('SKU', (
            <input style={inputStyle} value={sku} onChange={(e) => setSku(e.target.value)} aria-label="SKU" />
          ))}
          {field('BRAND', (
            <input style={inputStyle} value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand" />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {field('PRICE', (
            <input style={inputStyle} value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" aria-label="Price" placeholder="0.00" />
          ))}
          {field('CURRENCY', (
            <input style={inputStyle} value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Currency" placeholder="USD" maxLength={3} />
          ))}
          {field('INVENTORY', (
            <input style={inputStyle} value={inventory} onChange={(e) => setInventory(e.target.value)} inputMode="numeric" aria-label="Inventory" />
          ))}
        </div>
        {field('AVAILABILITY', (
          <select
            style={inputStyle}
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
            aria-label="Availability"
          >
            {AVAILABILITY_OPTIONS.map((opt) => (
              <option key={opt || 'none'} value={opt}>
                {opt === '' ? '—' : formatAvailability(opt as CatalogProduct['availability'])}
              </option>
            ))}
          </select>
        ))}
        {field('DESCRIPTION', (
          <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" />
        ))}
        {field('IMAGE URL', (
          <input style={inputStyle} value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} aria-label="Image URL" />
        ))}
        {field('PRODUCT URL', (
          <input style={inputStyle} value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Product URL" />
        ))}

        {error !== null ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>{error}</div>
        ) : null}

        <div className="flex items-center justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5"
            style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: sansFont, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={busy}
            className="px-3 py-1.5"
            style={{ background: C.lime, color: '#0a0a0a', border: 'none', borderRadius: 2, fontFamily: sansFont, fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Confirm modal for deleting a product. */
function DeleteProductModal({
  C,
  product,
  onClose,
  deleteProduct,
  onDeleted,
}: {
  C: ThemeC;
  product: CatalogProduct;
  onClose: () => void;
  deleteProduct: (id: string) => Promise<DeleteProductResult>;
  onDeleted: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fromCart = !isEditableSource(product.source);
  // Mount guard — avoid setState-after-unmount if the dialog is closed
  // (e.g. by Escape) while the DELETE is in flight.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const onConfirm = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    const res = await deleteProduct(product.catalog_product_id);
    if (!aliveRef.current) return;
    setBusy(false);
    switch (res.kind) {
      case 'ok':
        onDeleted();
        return;
      case 'not_found':
        // Already gone — treat as success (the list will reflect it).
        onDeleted();
        return;
      case 'forbidden':
        setError('Deleting requires owner or admin.');
        return;
      case 'error':
        setError(res.message);
        return;
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Delete product"
      closeOnBackdrop={false}
    >
      <div className="flex flex-col gap-3 p-6">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
          Remove <strong>{product.title}</strong> from the catalog?
        </div>
        {fromCart ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            This product is synced from {formatSource(product.source)} — it will reappear on the next
            sync. To remove it permanently, delete it in {formatSource(product.source)}.
          </div>
        ) : null}
        {error !== null ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>{error}</div>
        ) : null}
        <div className="flex items-center justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5"
            style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: sansFont, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={busy}
            className="px-3 py-1.5 inline-flex items-center gap-2"
            style={{ background: C.coralDim, color: C.coralFg, border: `1px solid rgba(255,107,71,0.3)`, borderRadius: 2, fontFamily: sansFont, fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
          >
            <Trash2 size={13} />
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * ONE effective-state eligibility pill (collapsing the previous SEARCH/CHECKOUT
 * pair). Lime "ELIGIBLE" when search-eligible, neutral "OFF" when not. The
 * tooltip carries the search/checkout breakdown + a derived reason.
 */
function EligibilityCell({
  C,
  product,
  hasCatalogRules,
}: {
  C: ThemeC;
  product: CatalogProduct;
  hasCatalogRules: boolean;
}): JSX.Element {
  const eligible = product.is_eligible_search;
  const checkout = product.is_eligible_checkout ? 'eligible' : 'blocked';
  const reason = eligible
    ? null
    : deriveSearchIneligibleReason(product.availability, hasCatalogRules);
  return (
    <Tooltip
      content={
        <span>
          <span style={{ fontWeight: 600 }}>
            Search: {eligible ? 'eligible' : 'blocked'}
          </span>
          <br />
          Checkout: {checkout}
          {reason !== null ? (
            <>
              <br />
              {reason}
            </>
          ) : null}
        </span>
      }
    >
      <Pill tone={eligible ? 'lime' : 'neutral'}>{eligible ? 'ELIGIBLE' : 'OFF'}</Pill>
    </Tooltip>
  );
}

/**
 * The single pagination bar — lives BELOW the table. Range status on the
 * left; rows-per-page + Prev/Next on the right. Wraps on narrow/mobile
 * viewports (`flex-wrap`). The Prev/Next enabled state is the sole signal
 * for "more pages" — no separate (non-functional) "MORE" label. The
 * Prev/Next pair is hidden on a single page; the rows-per-page selector
 * always shows so it is reachable even when everything fits one page.
 */
function Pager({
  C,
  offset,
  count,
  pageSize,
  hasMore,
  onPage,
  onPageSize,
}: {
  C: ThemeC;
  offset: number;
  count: number;
  pageSize: number;
  hasMore: boolean;
  onPage: (nextOffset: number) => void;
  onPageSize: (size: number) => void;
}): JSX.Element {
  const canPrev = offset > 0;
  const multiPage = canPrev || hasMore;
  const start = count === 0 ? 0 : offset + 1;
  const end = offset + count;
  const btn = (label: string, disabled: boolean, onClick: () => void): JSX.Element => (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1,
        color: disabled ? C.dim : C.text,
        background: 'transparent',
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: '6px 12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      className="px-5 py-3 flex flex-wrap items-center justify-between gap-3"
      style={{ borderTop: `1px solid ${C.borderSoft}` }}
    >
      <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
        {start}–{end}
      </span>
      <div className="flex items-center gap-2">
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label="Rows per page"
          style={{
            padding: '6px 8px',
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            color: C.text,
            fontFamily: sansFont,
            fontSize: 12,
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
        {multiPage ? (
          <>
            {btn('← PREV', !canPrev, () => onPage(offset - pageSize))}
            {btn('NEXT →', !hasMore, () => onPage(offset + count))}
          </>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data-source strip — a thin bordered strip directly under the table.
// Ghost "Sync from Shopify" (full sync-now state machine preserved), a
// "last synced" caption, ghost "Upload CSV" (import preserved), and a
// client-side "Download template". Results render inline below the controls.
// ---------------------------------------------------------------------------

/** Newest `last_synced_at` across a page (epoch ms), or 0 when empty. Used
 *  as the pre-sync watermark so polling can detect the background sync
 *  landing fresh rows. */
function newestSyncedAt(page: CatalogPage | null): number {
  if (page === null) return 0;
  let max = 0;
  for (const p of page.products) {
    const t = Date.parse(p.last_synced_at);
    if (Number.isFinite(t) && t > max) max = t;
  }
  return max;
}

type SyncState =
  | { kind: 'idle' }
  | { kind: 'syncing' }
  | { kind: 'done' }
  | { kind: 'timeout' }
  | { kind: 'error'; message: string };

type UploadState =
  | { kind: 'idle' }
  | { kind: 'reading' }
  | { kind: 'uploading' }
  | { kind: 'done'; summary: CatalogImportSummary }
  | { kind: 'error'; message: string };

/** Trigger a client-side download of the CSV import template. */
function downloadTemplate(): void {
  const blob = new Blob([CATALOG_TEMPLATE_CSV], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sill-catalog-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DataSourceStrip({
  C,
  shopifyConnected,
  lastSyncedAt,
  startSync,
  pollCatalog,
  currentPage,
  uploadCsv,
  onSynced,
  onImported,
}: {
  C: ThemeC;
  /** Show the Shopify-sync control + auto-sync caption only when the site has a
   *  connected Shopify integration; CSV upload is always available. */
  shopifyConnected: boolean;
  lastSyncedAt: string | null;
  startSync: () => Promise<import('../../api/catalog.js').SyncNowResult>;
  pollCatalog: () => Promise<import('../../api/catalog.js').GetCatalogResult>;
  currentPage: CatalogPage | null;
  uploadCsv: (csv: string) => Promise<import('../../api/catalog.js').ImportCatalogResult>;
  onSynced: () => void;
  onImported: () => void;
}): JSX.Element {
  const [sync, setSync] = useState<SyncState>({ kind: 'idle' });
  const [upload, setUpload] = useState<UploadState>({ kind: 'idle' });
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Track mount so the polling loop / async upload never setState after
  // unmount or a site swap.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const onSyncClick = useCallback(async (): Promise<void> => {
    const baseline = newestSyncedAt(currentPage);
    setSync({ kind: 'syncing' });
    const res = await startSync();
    if (!aliveRef.current) return;
    switch (res.kind) {
      case 'started':
        break; // fall through to polling below
      case 'rate_limited':
        setSync({
          kind: 'error',
          message:
            res.reason === 'daily_cap'
              ? 'Daily sync limit reached for this account. Try again tomorrow.'
              : res.reason === 'sync_busy'
                ? 'The sync system is busy right now — try again in a moment.'
                : 'A sync ran very recently — try again in a few minutes.',
        });
        return;
      case 'in_progress':
        setSync({ kind: 'error', message: 'A sync is already running for this account.' });
        return;
      case 'forbidden':
        setSync({ kind: 'error', message: 'Syncing requires owner or admin.' });
        return;
      case 'not_found':
        setSync({ kind: 'error', message: 'Site not found.' });
        return;
      case 'unavailable':
        setSync({ kind: 'error', message: 'Shopify sync is not available on this deployment.' });
        return;
      case 'error':
        setSync({ kind: 'error', message: res.message });
        return;
    }

    // 202 accepted — poll the catalog every ~3s (cap ~10) until the newest
    // `last_synced_at` advances past the pre-sync baseline, then refresh.
    for (let attempt = 0; attempt < SYNC_POLL_MAX_ATTEMPTS; attempt++) {
      await sleep(SYNC_POLL_INTERVAL_MS);
      if (!aliveRef.current) return;
      const poll = await pollCatalog();
      if (!aliveRef.current) return;
      if (poll.kind === 'ok' && newestSyncedAt(poll.page) > baseline) {
        setSync({ kind: 'done' });
        onSynced();
        return;
      }
      // A transient poll error doesn't abort the loop — the next tick retries.
    }
    // Polled out the cap without observing fresh rows: the sync may still be
    // running (large catalog) or may have produced no changes. Surface a soft
    // "still running" state and refresh the list once so any progress shows.
    if (!aliveRef.current) return;
    setSync({ kind: 'timeout' });
    onSynced();
  }, [currentPage, startSync, pollCatalog, onSynced]);

  const onFile = useCallback(
    async (file: File): Promise<void> => {
      if (file.size > MAX_CSV_BYTES) {
        setUpload({ kind: 'error', message: 'That file is larger than the 8 MB limit.' });
        return;
      }
      setUpload({ kind: 'reading' });
      let csv: string;
      try {
        csv = await file.text();
      } catch {
        setUpload({ kind: 'error', message: 'Could not read that file.' });
        return;
      }
      setUpload({ kind: 'uploading' });
      const res = await uploadCsv(csv);
      if (!aliveRef.current) return;
      switch (res.kind) {
        case 'ok':
          setUpload({ kind: 'done', summary: res.summary });
          onImported();
          break;
        case 'too_large':
          setUpload({ kind: 'error', message: 'That file is larger than the 8 MB limit.' });
          break;
        case 'unsupported_type':
          setUpload({ kind: 'error', message: 'Upload a .csv file (text/csv).' });
          break;
        case 'forbidden':
          setUpload({ kind: 'error', message: 'Importing requires owner or admin.' });
          break;
        case 'not_found':
          setUpload({ kind: 'error', message: 'Site not found.' });
          break;
        case 'bad_request':
          setUpload({
            kind: 'error',
            message: res.reason !== undefined ? `Import rejected: ${res.reason}.` : 'Import rejected.',
          });
          break;
        case 'error':
          setUpload({ kind: 'error', message: res.message });
          break;
      }
      // Reset the file input so re-selecting the same file fires onChange.
      if (inputRef.current) inputRef.current.value = '';
    },
    [uploadCsv, onImported],
  );

  const syncBusy = sync.kind === 'syncing';
  const uploadBusy = upload.kind === 'reading' || upload.kind === 'uploading';

  const ghost: CSSProperties = {
    background: 'transparent',
    color: C.text,
    border: `1px solid ${C.border}`,
    fontFamily: sansFont,
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 2,
    cursor: 'pointer',
    width: 'fit-content',
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, background: C.surface }}>
      <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {shopifyConnected ? (
            <button
              type="button"
              onClick={() => void onSyncClick()}
              disabled={syncBusy}
              className="inline-flex items-center gap-2 px-3 py-1.5"
              style={{ ...ghost, opacity: syncBusy ? 0.6 : 1, cursor: syncBusy ? 'not-allowed' : 'pointer' }}
            >
              <RefreshCw size={13} className={syncBusy ? 'animate-spin' : undefined} />
              {syncBusy ? 'Syncing…' : 'Sync from Shopify'}
            </button>
          ) : null}

          <label
            className="inline-flex items-center gap-2 px-3 py-1.5"
            style={{ ...ghost, opacity: uploadBusy ? 0.6 : 1, cursor: uploadBusy ? 'not-allowed' : 'pointer' }}
          >
            <Upload size={13} />
            {upload.kind === 'reading'
              ? 'Reading…'
              : upload.kind === 'uploading'
                ? 'Uploading…'
                : 'Upload CSV'}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              disabled={uploadBusy}
              aria-label="CSV file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) void onFile(file);
              }}
            />
          </label>

          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 px-2 py-1.5"
            style={{
              background: 'transparent',
              color: C.muted,
              border: 'none',
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Download size={11} />
            DOWNLOAD TEMPLATE
          </button>
        </div>

        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            color: C.muted,
            whiteSpace: 'nowrap',
          }}
        >
          LAST SYNCED {formatLastSynced(lastSyncedAt).toUpperCase()}
          {shopifyConnected ? ' · AUTO-SYNCS HOURLY' : ''}
        </span>
      </div>

      {sync.kind === 'error' || sync.kind === 'done' || sync.kind === 'timeout' ? (
        <div className="px-5 pb-3">
          {sync.kind === 'error' ? (
            <div
              className="p-3 flex items-start gap-2"
              style={{ background: C.coralDim, border: `1px solid rgba(255,107,71,0.3)`, borderRadius: 2 }}
            >
              <AlertOctagon size={13} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                {sync.message}
              </span>
            </div>
          ) : null}
          {sync.kind === 'done' ? (
            <div
              className="p-3 flex items-center gap-2"
              style={{ background: C.mintDim, border: `1px solid rgba(90,255,205,0.3)`, borderRadius: 2 }}
            >
              <CheckCircle2 size={13} color={C.mintFg} />
              <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                Catalog refreshed from Shopify.
              </span>
            </div>
          ) : null}
          {sync.kind === 'timeout' ? (
            <div
              className="p-3 flex items-start gap-2"
              style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
            >
              <RefreshCw size={13} color={C.muted} className="flex-shrink-0 mt-0.5" />
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                Sync is still running — large catalogs can take a little longer. The list
                will update as products land; re-open this view to check progress.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {upload.kind === 'error' || upload.kind === 'done' ? (
        <div className="px-5 pb-3">
          {upload.kind === 'error' ? (
            <div
              className="p-3 flex items-start gap-2"
              style={{ background: C.coralDim, border: `1px solid rgba(255,107,71,0.3)`, borderRadius: 2 }}
            >
              <AlertOctagon size={13} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                {upload.message}
              </span>
            </div>
          ) : null}
          {upload.kind === 'done' ? (
            <div
              className="p-3"
              style={{ background: C.mintDim, border: `1px solid rgba(90,255,205,0.3)`, borderRadius: 2 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={13} color={C.mintFg} />
                <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                  Import complete
                </span>
              </div>
              <div className="flex items-center gap-4 flex-wrap" style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                <span>SYNCED {upload.summary.synced}</span>
                <span>STALE {upload.summary.stale}</span>
                <span style={{ color: upload.summary.errors > 0 ? C.coralFg : C.muted }}>
                  ERRORS {upload.summary.errors}
                </span>
                {upload.summary.deduped > 0 ? (
                  <Tooltip content="Rows skipped — their SKU already belongs to a connected store's product">
                    <span>DEDUPED {upload.summary.deduped}</span>
                  </Tooltip>
                ) : null}
                {upload.summary.truncated ? <span style={{ color: C.amberFg }}>TRUNCATED</span> : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Eligibility — "suggested → review → accept" (ADR-0103).
//
// Reads the site's current catalog rules + the suggested default + counts. When
// unconfigured, shows the suggestion in plain language, a LIVE preview of its
// effect, and an "Accept & activate" button (the SINGLE lime action on the
// page); activating publishes the policy server-side, refreshes the counts, and
// refreshes the product list so the eligibility badges update. When configured,
// shows a compact active summary with a subtle re-apply affordance. Full rule
// editing is a later slice.
// ---------------------------------------------------------------------------

type EligibilityLoad =
  | { kind: 'loading' }
  | { kind: 'ready'; state: EligibilityState }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

type PreviewLoad =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; would_search_eligible: number; total: number }
  | { kind: 'error' };

type ActivateState =
  | { kind: 'idle' }
  | { kind: 'activating' }
  | { kind: 'done'; search_eligible: number }
  | { kind: 'error'; message: string };

function EligibilitySection({
  C,
  load,
  preview,
  activate,
  onActivated,
  onConfiguredChange,
}: {
  C: ThemeC;
  load: () => Promise<import('../../api/catalog.js').GetEligibilityResult>;
  preview: (
    rules: readonly import('@sill/types').Rule[],
  ) => Promise<import('../../api/catalog.js').PreviewEligibilityResult>;
  activate: (
    rules: readonly import('@sill/types').Rule[],
  ) => Promise<import('../../api/catalog.js').ActivateEligibilityResult>;
  onActivated: () => void;
  onConfiguredChange: (configured: boolean) => void;
}): JSX.Element {
  const [loadState, setLoadState] = useState<EligibilityLoad>({ kind: 'loading' });
  const [previewState, setPreviewState] = useState<PreviewLoad>({ kind: 'idle' });
  const [activateState, setActivateState] = useState<ActivateState>({ kind: 'idle' });
  const aliveRef = useRef(true);

  const reload = useCallback(async (): Promise<void> => {
    setLoadState({ kind: 'loading' });
    const res = await load();
    if (!aliveRef.current) return;
    switch (res.kind) {
      case 'ok':
        setLoadState({ kind: 'ready', state: res.state });
        break;
      case 'forbidden':
        setLoadState({ kind: 'forbidden' });
        break;
      case 'not_found':
        setLoadState({ kind: 'error', message: 'Site not found.' });
        break;
      case 'error':
        setLoadState({ kind: 'error', message: res.message });
        break;
    }
  }, [load]);

  useEffect(() => {
    aliveRef.current = true;
    void reload();
    return () => {
      aliveRef.current = false;
    };
  }, [reload]);

  // When unconfigured (no current rules), dry-run the suggestion so the review
  // step can show "Z of N products would be discoverable". Re-runs whenever the
  // loaded suggestion changes.
  const isConfigured =
    loadState.kind === 'ready' && loadState.state.current_rules.length > 0;
  const suggestion =
    loadState.kind === 'ready' ? loadState.state.suggestion : null;
  // ADR-0112 §4 — checkout is a stricter, SEPARATE gate: it needs a
  // checkout-scoped catalog rule (this) ON TOP of a connected rail +
  // compliance. Derive whether a checkout rule is already active.
  const checkoutConfigured =
    loadState.kind === 'ready'
    && loadState.state.current_rules.some(catalogRuleIsCheckout);

  // Report the configured flag up so the products table's eligibility tooltip
  // can derive "not activated yet" vs "not matched by the rules".
  useEffect(() => {
    if (loadState.kind === 'ready') onConfiguredChange(isConfigured);
  }, [loadState.kind, isConfigured, onConfiguredChange]);

  useEffect(() => {
    if (loadState.kind !== 'ready' || isConfigured || suggestion === null) {
      return;
    }
    if (suggestion.length === 0) {
      setPreviewState({ kind: 'error' });
      return;
    }
    let cancelled = false;
    setPreviewState({ kind: 'loading' });
    void (async () => {
      const res = await preview(suggestion);
      if (cancelled || !aliveRef.current) return;
      if (res.kind === 'ok') {
        setPreviewState({
          kind: 'ready',
          would_search_eligible: res.preview.would_search_eligible,
          total: res.preview.total,
        });
      } else {
        setPreviewState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadState.kind, isConfigured, suggestion, preview]);

  const onActivate = useCallback(
    async (rules: readonly import('@sill/types').Rule[]): Promise<void> => {
      setActivateState({ kind: 'activating' });
      const res = await activate(rules);
      if (!aliveRef.current) return;
      switch (res.kind) {
        case 'ok':
          setActivateState({ kind: 'done', search_eligible: res.activation.counts.search_eligible });
          // Refresh the eligibility summary (new counts) + the product list (so
          // the per-row eligibility badges update).
          void reload();
          onActivated();
          break;
        case 'conflict':
          setActivateState({
            kind: 'error',
            message: 'Someone just changed this policy — refresh and try again.',
          });
          break;
        case 'forbidden':
          setActivateState({ kind: 'error', message: 'Activating eligibility requires owner or admin.' });
          break;
        case 'not_found':
          setActivateState({ kind: 'error', message: 'Site not found.' });
          break;
        case 'bad_request':
          setActivateState({
            kind: 'error',
            message:
              res.reason !== undefined
                ? `The eligibility rule was rejected: ${res.reason}.`
                : 'The eligibility rule was rejected.',
          });
          break;
        case 'error':
          setActivateState({ kind: 'error', message: res.message });
          break;
      }
    },
    [activate, onActivated, reload],
  );

  if (loadState.kind === 'forbidden') return <ForbiddenCard C={C} />;

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-4 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          ELIGIBILITY
        </div>
        <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, marginBottom: 4 }}>
          Discoverable to agents
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          Which products verified AI agents can find in search, and which are
          checkout-eligible. Eligibility is policy-derived — your activation is
          the recorded decision.
        </div>
      </div>

      {loadState.kind === 'loading' ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Reading eligibility…
        </div>
      ) : null}

      {loadState.kind === 'error' ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5 }}>
          Couldn&apos;t load eligibility — {loadState.message}
        </div>
      ) : null}

      {loadState.kind === 'ready' ? (
        <div className="space-y-4">
          <EligibilitySummary C={C} counts={loadState.state.counts} />

          {isConfigured ? (
            <ConfiguredEligibility
              C={C}
              onReapply={() => void onActivate(loadState.state.suggestion)}
              busy={activateState.kind === 'activating'}
              canReapply={loadState.state.suggestion.length > 0}
            />
          ) : (
            <UnconfiguredEligibility
              C={C}
              previewState={previewState}
              onActivate={() => void onActivate(loadState.state.suggestion)}
              busy={activateState.kind === 'activating'}
              canActivate={loadState.state.suggestion.length > 0}
            />
          )}

          <CheckoutEligibility
            C={C}
            siteContext={loadState.state.site_context}
            searchConfigured={isConfigured}
            checkoutConfigured={checkoutConfigured}
            busy={activateState.kind === 'activating'}
            canEnable={loadState.state.checkout_suggestion.length > 0}
            onEnable={() =>
              void onActivate([
                ...loadState.state.current_rules,
                ...loadState.state.checkout_suggestion,
              ])
            }
          />

          {activateState.kind === 'error' ? (
            <div
              className="p-3 flex items-start gap-2"
              style={{ background: C.coralDim, border: `1px solid rgba(255,107,71,0.3)`, borderRadius: 2 }}
            >
              <AlertOctagon size={13} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                {activateState.message}
              </span>
            </div>
          ) : null}

          {activateState.kind === 'done' ? (
            <div
              className="p-3 flex items-center gap-2"
              style={{ background: C.mintDim, border: `1px solid rgba(90,255,205,0.3)`, borderRadius: 2 }}
            >
              <CheckCircle2 size={13} color={C.mintFg} />
              <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                Eligibility activated — {activateState.search_eligible}{' '}
                {activateState.search_eligible === 1 ? 'product' : 'products'} now discoverable.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** One-line summary of the persisted counts. */
function EligibilitySummary({
  C,
  counts,
}: {
  C: ThemeC;
  counts: import('../../api/catalog.js').EligibilityCounts;
}): JSX.Element {
  return (
    <div
      className="flex items-center gap-4 flex-wrap"
      style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.5 }}
    >
      <span>
        <strong style={{ fontWeight: 600 }}>{counts.search_eligible}</strong> of {counts.total}{' '}
        {counts.total === 1 ? 'product' : 'products'} discoverable in search
      </span>
      <span style={{ color: C.muted }}>·</span>
      <span style={{ color: C.muted }}>
        {counts.checkout_eligible} checkout-eligible
      </span>
    </div>
  );
}

/** Unconfigured state: the suggestion in plain language + a live preview + accept. */
function UnconfiguredEligibility({
  C,
  previewState,
  onActivate,
  busy,
  canActivate,
}: {
  C: ThemeC;
  previewState: PreviewLoad;
  onActivate: () => void;
  busy: boolean;
  canActivate: boolean;
}): JSX.Element {
  return (
    <div
      className="p-4"
      style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} color={C.limeFg} />
        <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
          Suggested: make in-stock products discoverable to AI agents
        </span>
      </div>
      <div
        className="mb-3"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}
      >
        Only products reported in stock become eligible for agent discovery
        (search). You can refine the rules later.
      </div>

      <div
        className="mb-3"
        style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 0.5 }}
      >
        {previewState.kind === 'loading' ? (
          <span>Previewing effect…</span>
        ) : previewState.kind === 'ready' ? (
          <span style={{ color: C.text }}>
            {previewState.would_search_eligible.toLocaleString('en-US')} of {previewState.total.toLocaleString('en-US')}{' '}
            {previewState.total === 1 ? 'product' : 'products'} would be discoverable
          </span>
        ) : previewState.kind === 'error' ? (
          <span style={{ color: C.amberFg }}>Preview unavailable.</span>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onActivate}
        disabled={busy || !canActivate}
        className="inline-flex items-center gap-2 px-4 py-2"
        style={{
          background: busy || !canActivate ? C.surface2 : C.lime,
          color: busy || !canActivate ? C.muted : C.ink,
          border: `1px solid ${busy || !canActivate ? C.border : C.lime}`,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          cursor: busy || !canActivate ? 'not-allowed' : 'pointer',
          width: 'fit-content',
        }}
      >
        <CheckCircle2 size={13} className={busy ? 'animate-spin' : undefined} />
        {busy ? 'Activating…' : 'Accept & activate'}
      </button>
    </div>
  );
}

/** Configured state: a compact "active" summary + a subtle re-apply affordance. */
function ConfiguredEligibility({
  C,
  onReapply,
  busy,
  canReapply,
}: {
  C: ThemeC;
  onReapply: () => void;
  busy: boolean;
  canReapply: boolean;
}): JSX.Element {
  return (
    <div
      className="p-4 flex items-start justify-between gap-3 flex-wrap"
      style={{ background: C.mintDim, border: `1px solid rgba(90,255,205,0.3)`, borderRadius: 2 }}
    >
      <div className="flex items-start gap-2">
        <CheckCircle2 size={14} color={C.mintFg} className="flex-shrink-0 mt-0.5" />
        <div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
            Catalog eligibility is active
          </div>
          <div
            className="mt-0.5"
            style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}
          >
            In-stock products are discoverable to agents. Re-apply to re-derive
            eligibility from the suggested rules.
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onReapply}
        disabled={busy || !canReapply}
        className="inline-flex items-center gap-1.5 px-3 py-1.5"
        style={{
          background: 'transparent',
          color: busy || !canReapply ? C.dim : C.text,
          border: `1px solid ${C.border}`,
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1,
          borderRadius: 2,
          cursor: busy || !canReapply ? 'not-allowed' : 'pointer',
          opacity: busy || !canReapply ? 0.5 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        <RefreshCw size={11} className={busy ? 'animate-spin' : undefined} />
        {busy ? 'RE-APPLYING…' : 'RE-APPLY'}
      </button>
    </div>
  );
}

/** ADR-0112 §4 — is this catalog rule scoped to checkout (or both)? */
function catalogRuleIsCheckout(rule: import('@sill/types').Rule): boolean {
  const scope = (rule.params as { applies_to?: unknown }).applies_to;
  return scope === 'checkout' || scope === 'both';
}

/**
 * ADR-0112 §4 — checkout eligibility. Checkout is a STRICTER, separate gate
 * than search: it needs a checkout-scoped catalog rule ON TOP of a connected
 * payment rail + the compliance fields. Only relevant once search is on.
 * States:
 *   - search not on yet           → nothing (activate search first).
 *   - checkout rule already active → confirmed.
 *   - rail/compliance missing      → say exactly what to add (no silent gap —
 *     the old hint hid here once both were present, leaving checkout blocked
 *     with no explanation because the RULE was still missing).
 *   - rail + compliance present, no checkout rule → the "Enable checkout"
 *     action (activates the checkout rule — the previously-missing piece).
 */
function CheckoutEligibility({
  C,
  siteContext,
  searchConfigured,
  checkoutConfigured,
  busy,
  canEnable,
  onEnable,
}: {
  C: ThemeC;
  siteContext: import('../../api/catalog.js').EligibilitySiteContext;
  searchConfigured: boolean;
  checkoutConfigured: boolean;
  busy: boolean;
  canEnable: boolean;
  onEnable: () => void;
}): JSX.Element | null {
  if (!searchConfigured) return null;

  if (checkoutConfigured) {
    return (
      <div
        className="p-3 flex items-start gap-2"
        style={{ background: C.mintDim, border: `1px solid rgba(90,255,205,0.3)`, borderRadius: 2 }}
      >
        <CheckCircle2 size={13} color={C.mintFg} className="flex-shrink-0 mt-0.5" />
        <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
          Checkout is enabled for in-stock products (with a connected rail +
          compliance, agents can buy).
        </span>
      </div>
    );
  }

  const needsRail = !siteContext.has_settlement_rail;
  const needsCompliance = !siteContext.has_compliance_fields;
  if (needsRail || needsCompliance) {
    const missing: string[] = [];
    if (needsRail) missing.push('a connected payment rail');
    if (needsCompliance) missing.push('the compliance fields below');
    return (
      <div
        className="p-3 flex items-start gap-2"
        style={{ background: C.amberDim, border: `1px solid rgba(255,182,39,0.3)`, borderRadius: 2 }}
      >
        <CreditCard size={13} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
        <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
          Discovery (search) is on. To also allow checkout, add {missing.join(' and ')},
          then turn on a checkout rule here.
        </span>
      </div>
    );
  }

  // Rail + compliance are present — the only remaining piece is the rule.
  return (
    <div
      className="p-4"
      style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <div className="flex items-center gap-2 mb-1">
        <CreditCard size={14} color={C.limeFg} />
        <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
          Enable checkout for in-stock products
        </span>
      </div>
      <div
        className="mb-3"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}
      >
        Your payment rail and compliance are ready. Activating a checkout rule
        makes in-stock products buyable by agents (subscriptions stay
        discovery-only).
      </div>
      <button
        type="button"
        onClick={onEnable}
        disabled={busy || !canEnable}
        className="inline-flex items-center gap-2 px-4 py-2"
        style={{
          background: busy || !canEnable ? C.surface2 : C.lime,
          color: busy || !canEnable ? C.muted : C.ink,
          border: `1px solid ${busy || !canEnable ? C.border : C.lime}`,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          cursor: busy || !canEnable ? 'not-allowed' : 'pointer',
          width: 'fit-content',
        }}
      >
        <CheckCircle2 size={13} className={busy ? 'animate-spin' : undefined} />
        {busy ? 'Activating…' : 'Enable checkout'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compliance form. Collapses to a compact "complete — edit" summary when all
// three fields are filled + valid (mirrors the eligibility configured/compact
// pattern); expands on click.
// ---------------------------------------------------------------------------

type ComplianceLoad =
  | { kind: 'loading' }
  | { kind: 'ready'; fields: SiteComplianceFields }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/** Map a server 400 `reason` for a compliance field to the field key. */
function fieldForReason(reason: string | undefined): keyof FormFields | null {
  switch (reason) {
    case 'invalid_seller_tos_url':
      return 'seller_tos_url';
    case 'invalid_seller_privacy_policy_url':
      return 'seller_privacy_policy_url';
    case 'invalid_returns_policy':
      return 'returns_policy';
    default:
      return null;
  }
}

interface FormFields {
  seller_tos_url: string;
  seller_privacy_policy_url: string;
  returns_policy: string;
}

/** Client-side https-only check, mirroring the server's `isValidComplianceUrl`. */
function isHttpsUrl(value: string): boolean {
  if (value.length === 0) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/** All three fields present + the two URLs https-valid → "complete". */
function complianceComplete(f: FormFields): boolean {
  const tos = f.seller_tos_url.trim();
  const privacy = f.seller_privacy_policy_url.trim();
  const returns = f.returns_policy.trim();
  return (
    tos.length > 0
    && privacy.length > 0
    && returns.length > 0
    && isHttpsUrl(tos)
    && isHttpsUrl(privacy)
  );
}

function ComplianceSection({
  C,
  load,
  save,
}: {
  C: ThemeC;
  load: () => Promise<import('../../api/catalog.js').GetComplianceResult>;
  save: (
    update: import('../../api/catalog.js').ComplianceUpdate,
  ) => Promise<import('../../api/catalog.js').PutComplianceResult>;
}): JSX.Element {
  const [loadState, setLoadState] = useState<ComplianceLoad>({ kind: 'loading' });
  const [fields, setFields] = useState<FormFields>({
    seller_tos_url: '',
    seller_privacy_policy_url: '',
    returns_policy: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormFields, string>>>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Collapsed when complete; expand on "edit". Starts collapsed for an
  // already-complete site (set in the load effect).
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadState({ kind: 'loading' });
    void (async () => {
      const res = await load();
      if (cancelled) return;
      switch (res.kind) {
        case 'ok': {
          setLoadState({ kind: 'ready', fields: res.compliance });
          const next: FormFields = {
            seller_tos_url: res.compliance.seller_tos_url ?? '',
            seller_privacy_policy_url: res.compliance.seller_privacy_policy_url ?? '',
            returns_policy: res.compliance.returns_policy ?? '',
          };
          setFields(next);
          // Start collapsed when the loaded fields are already complete.
          setExpanded(!complianceComplete(next));
          break;
        }
        case 'forbidden':
          setLoadState({ kind: 'forbidden' });
          break;
        case 'not_found':
          setLoadState({ kind: 'error', message: 'Site not found.' });
          break;
        case 'error':
          setLoadState({ kind: 'error', message: res.message });
          break;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const setField = (key: keyof FormFields, value: string): void => {
    setFields((cur) => ({ ...cur, [key]: value }));
    setFieldErrors((cur) => {
      if (cur[key] === undefined) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
    setSavedAt(null);
  };

  const onSave = useCallback(async (): Promise<void> => {
    // Client-side validation first — mirror the server's https-only rule
    // so a bad URL surfaces inline rather than as a round-trip 400.
    const errs: Partial<Record<keyof FormFields, string>> = {};
    const tos = fields.seller_tos_url.trim();
    const privacy = fields.seller_privacy_policy_url.trim();
    if (tos.length > 0 && !isHttpsUrl(tos)) {
      errs.seller_tos_url = 'Must be an https:// URL.';
    }
    if (privacy.length > 0 && !isHttpsUrl(privacy)) {
      errs.seller_privacy_policy_url = 'Must be an https:// URL.';
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    // Send a string to set, null to clear (empty input = clear).
    const update: import('../../api/catalog.js').ComplianceUpdate = {
      seller_tos_url: tos.length > 0 ? tos : null,
      seller_privacy_policy_url: privacy.length > 0 ? privacy : null,
      returns_policy:
        fields.returns_policy.trim().length > 0 ? fields.returns_policy.trim() : null,
    };

    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    const res = await save(update);
    setSaving(false);
    switch (res.kind) {
      case 'ok': {
        setSavedAt(Date.now());
        setLoadState({ kind: 'ready', fields: res.compliance });
        const saved: FormFields = {
          seller_tos_url: res.compliance.seller_tos_url ?? '',
          seller_privacy_policy_url: res.compliance.seller_privacy_policy_url ?? '',
          returns_policy: res.compliance.returns_policy ?? '',
        };
        // Collapse to the compact summary once a complete set is saved.
        if (complianceComplete(saved)) setExpanded(false);
        break;
      }
      case 'bad_request': {
        const key = fieldForReason(res.reason);
        if (key !== null) {
          setFieldErrors({
            [key]:
              key === 'returns_policy'
                ? 'The server rejected this value.'
                : 'Must be an https:// URL.',
          });
        } else {
          setSaveError('The compliance update was rejected.');
        }
        break;
      }
      case 'forbidden':
        setSaveError('Saving requires owner or admin.');
        break;
      case 'not_found':
        setSaveError('Site not found.');
        break;
      case 'error':
        setSaveError(res.message);
        break;
    }
  }, [fields, save]);

  if (loadState.kind === 'forbidden') return <ForbiddenCard C={C} />;

  const isComplete = loadState.kind === 'ready' && complianceComplete(fields);

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-4 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          COMPLIANCE
        </div>
        <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, marginBottom: 4 }}>
          Checkout-gated compliance
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          The seller terms, privacy policy, and returns policy projected alongside
          checkout-eligible products. URLs must be https.
        </div>
      </div>

      {loadState.kind === 'loading' ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Reading compliance…
        </div>
      ) : null}

      {loadState.kind === 'error' ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5 }}>
          Couldn&apos;t load compliance — {loadState.message}
        </div>
      ) : null}

      {loadState.kind === 'ready' && isComplete && !expanded ? (
        <div
          className="p-3 flex items-center justify-between gap-3 flex-wrap"
          style={{ background: C.mintDim, border: `1px solid rgba(90,255,205,0.3)`, borderRadius: 2 }}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} color={C.mintFg} className="flex-shrink-0" />
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
              Compliance complete
            </span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              borderRadius: 2,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            EDIT
          </button>
        </div>
      ) : null}

      {loadState.kind === 'ready' && (!isComplete || expanded) ? (
        <div className="space-y-4">
          <UrlField
            C={C}
            id="compliance-seller-tos-url"
            label="Seller terms of service URL"
            placeholder="https://example.com/terms"
            value={fields.seller_tos_url}
            error={fieldErrors.seller_tos_url}
            onChange={(v) => setField('seller_tos_url', v)}
          />
          <UrlField
            C={C}
            id="compliance-seller-privacy-url"
            label="Seller privacy policy URL"
            placeholder="https://example.com/privacy"
            value={fields.seller_privacy_policy_url}
            error={fieldErrors.seller_privacy_policy_url}
            onChange={(v) => setField('seller_privacy_policy_url', v)}
          />
          <TextField
            C={C}
            id="compliance-returns-policy"
            label="Returns policy"
            placeholder="30-day returns on unopened items…"
            value={fields.returns_policy}
            error={fieldErrors.returns_policy}
            onChange={(v) => setField('returns_policy', v)}
          />

          {saveError !== null ? (
            <div
              className="p-3 flex items-start gap-2"
              style={{ background: C.coralDim, border: `1px solid rgba(255,107,71,0.3)`, borderRadius: 2 }}
            >
              <AlertOctagon size={13} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                {saveError}
              </span>
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2"
              style={{
                background: 'transparent',
                color: saving ? C.muted : C.text,
                border: `1px solid ${C.border}`,
                fontFamily: sansFont,
                fontWeight: 500,
                fontSize: 13,
                borderRadius: 2,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save compliance'}
            </button>
            {savedAt !== null ? (
              <span
                role="status"
                className="flex items-center gap-1.5"
                style={{ fontFamily: monoFont, fontSize: 10, color: C.mintFg, letterSpacing: 1 }}
              >
                <CheckCircle2 size={12} /> SAVED
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function UrlField(props: {
  C: ThemeC;
  id: string;
  label: string;
  placeholder: string;
  value: string;
  error: string | undefined;
  onChange: (v: string) => void;
}): JSX.Element {
  return <TextField {...props} type="url" />;
}

function TextField({
  C,
  id,
  label,
  placeholder,
  value,
  error,
  onChange,
  type = 'text',
}: {
  C: ThemeC;
  id: string;
  label: string;
  placeholder: string;
  value: string;
  error: string | undefined;
  onChange: (v: string) => void;
  type?: string;
}): JSX.Element {
  const invalid = error !== undefined;
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        {label.toUpperCase()}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={invalid}
        style={{
          width: '100%',
          maxWidth: 480,
          padding: '8px 10px',
          background: C.surface2,
          border: `1px solid ${invalid ? C.coralFg : C.border}`,
          borderRadius: 2,
          color: C.text,
          fontFamily: sansFont,
          fontSize: 13,
          outline: 'none',
        }}
      />
      {invalid ? (
        <div
          className="mt-1.5"
          style={{ fontFamily: sansFont, fontSize: 11, color: C.coralFg, lineHeight: 1.4 }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}
